import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import ListPagination from "../../containers/ListPagination";
import { PATHS } from "../../constants/paths";
import { keepState, returnState } from "../../utils/returnTo";
import { PAGES_TITLES } from "../../constants/titles";
import {
  auditLine,
  CHANNEL_LABELS,
  messageTemplateApi,
} from "../../utils/messageTemplateApi";

const useStyles = makeStyles({
  page: {
    backgroundColor: "#f7f9fc",
    minHeight: "calc(100vh - 64px)",
    paddingTop: "24px",
    paddingBottom: "24px",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px",
    flexWrap: "wrap",
  },
  title: { fontSize: "28px", fontWeight: 700, color: "#101828" },
  subtitle: {
    marginTop: "4px",
    fontSize: "14px",
    color: "#475467",
    maxWidth: "720px",
  },
  filters: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    padding: "16px",
    marginBottom: "12px",
  },
  filterField: { minWidth: "220px" },
  cell: { padding: "8px", fontSize: "13px", verticalAlign: "top" },
  headerCell: {
    padding: "8px",
    fontSize: "12px",
    fontWeight: 700,
    backgroundColor: "#f2f5fa",
  },
  mono: { fontFamily: "monospace", fontSize: "12px" },
  languages: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    listStyle: "none",
    margin: 0,
    padding: 0,
    maxWidth: "360px",
  },
  languageMatch: { backgroundColor: "#dbe7ff", fontWeight: 700 },
  touch: { minHeight: "44px", minWidth: "44px" },
  empty: { textAlign: "center", padding: "32px 12px", color: "#475467" },
  progressWrap: {
    display: "flex",
    justifyContent: "center",
    padding: "32px 0",
  },
});

const DEFAULT_SIZE = 50;

const SORTABLE = [
  { key: "TemplateName", label: "Template" },
  { key: "Description", label: "Description" },
  { key: "UpdatedAt", label: "Last change" },
];

const readQuery = (search) => {
  const sp = new URLSearchParams(search);
  return {
    templateName: sp.get("templateName") || "",
    localeId: sp.get("localeId") || "",
    status: sp.get("status") || "ACTIVE",
    query: sp.get("query") || "",
    sort: sp.get("sort") || "TemplateName",
    isAscending: sp.get("isAscending") !== "false",
    page: Number(sp.get("page")) || 0,
    size: Number(sp.get("size")) || DEFAULT_SIZE,
  };
};

const writeQuery = (state) => {
  const sp = new URLSearchParams();
  if (state.templateName) sp.set("templateName", state.templateName);
  // Written even when empty: "localeId=" records an explicit "All locales" so a reload does
  // not re-apply the signed-in admin's default locale.
  sp.set("localeId", state.localeId || "");
  if (state.status && state.status !== "ACTIVE") sp.set("status", state.status);
  if (state.query) sp.set("query", state.query);
  if (state.sort && state.sort !== "TemplateName") sp.set("sort", state.sort);
  if (!state.isAscending) sp.set("isAscending", "false");
  if (state.page) sp.set("page", String(state.page));
  if (state.size && state.size !== DEFAULT_SIZE)
    sp.set("size", String(state.size));
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
};

const MessageTemplates = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState(() => readQuery(location.search));
  // With no locale in the URL, wait for /locales so the first request already uses the admin's
  // own locale instead of loading every locale and then reloading.
  const [ready, setReady] = useState(() =>
    new URLSearchParams(location.search).has("localeId"),
  );
  const [searchDraft, setSearchDraft] = useState(state.query);
  const [definitions, setDefinitions] = useState([]);
  const [locales, setLocales] = useState([]);
  const [result, setResult] = useState({
    messageTemplates: [],
    totalElements: 0,
    currentPage: 0,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      messageTemplateApi.definitions(),
      messageTemplateApi.locales(),
    ])
      .then(([defs, locs]) => {
        setDefinitions(defs || []);
        setLocales(locs || []);
        const mine = (locs || []).find((l) => l.current);
        if (mine) {
          setState((prev) =>
            new URLSearchParams(window.location.search).has("localeId")
              ? prev
              : { ...prev, localeId: mine.code, page: 0 },
          );
        }
      })
      .catch((e) => toast.error(e.message))
      .finally(() => setReady(true));
  }, []);

  // Debounce free-text search so each keystroke is not a request.
  useEffect(() => {
    const handle = setTimeout(() => {
      setState((prev) =>
        prev.query === searchDraft.trim()
          ? prev
          : { ...prev, query: searchDraft.trim(), page: 0 },
      );
    }, 350);
    return () => clearTimeout(handle);
  }, [searchDraft]);

  const load = useCallback(() => {
    setLoading(true);
    messageTemplateApi
      .page(state)
      .then((data) =>
        setResult(
          data || { messageTemplates: [], totalElements: 0, currentPage: 0 },
        ),
      )
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [state]);

  useEffect(() => {
    if (!ready) return;
    load();
    const nextSearch = writeQuery(state);
    if (nextSearch !== location.search) {
      navigate(
        { pathname: location.pathname, search: nextSearch },
        { replace: true, state: keepState(location) },
      );
    }
    // location is intentionally not a dependency: this effect writes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, ready]);

  // Forward navigation that remembers this list's filters for the way back.
  const go = (to) => navigate(to, { state: returnState(location) });

  const patch = (changes) =>
    setState((prev) => ({ ...prev, page: 0, ...changes }));

  const onSort = (key) =>
    setState((prev) => ({
      ...prev,
      page: 0,
      sort: key,
      isAscending: prev.sort === key ? !prev.isAscending : true,
    }));

  const definitionByName = useMemo(
    () => Object.fromEntries(definitions.map((d) => [d.templateName, d])),
    [definitions],
  );

  const rows = result.messageTemplates || [];

  return (
    <Box className={classes.page}>
      <Container maxWidth="xl">
        <Box className={classes.header}>
          <Box>
            <Typography component="h1" className={classes.title}>
              {PAGES_TITLES.MESSAGE_TEMPLATES}
            </Typography>
            <Typography className={classes.subtitle}>
              One row per message; each has a version per language. Recipients
              get their own language, falling back to a related language and
              then en-US. Every save is kept as a revision you can restore.
            </Typography>
          </Box>
          <Box display="flex" style={{ gap: 12 }} flexWrap="wrap">
            <Button
              variant="outlined"
              className={classes.touch}
              onClick={() => go(`${PATHS.MESSAGE_TEMPLATES}/variable-requests`)}
            >
              Requested variables
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.touch}
              onClick={() =>
                go(
                  `${PATHS.MESSAGE_TEMPLATES}/new${
                    state.templateName
                      ? `?templateName=${encodeURIComponent(
                          state.templateName,
                        )}`
                      : ""
                  }`,
                )
              }
            >
              New template
            </Button>
          </Box>
        </Box>

        <Paper
          className={classes.filters}
          component="section"
          aria-label="Filters"
        >
          <TextField
            select
            label="Template"
            variant="outlined"
            size="small"
            className={classes.filterField}
            value={state.templateName}
            onChange={(e) => patch({ templateName: e.target.value })}
          >
            <MenuItem value="">All templates</MenuItem>
            {definitions.map((d) => (
              <MenuItem key={d.templateName} value={d.templateName}>
                {d.templateName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Has language"
            variant="outlined"
            size="small"
            className={classes.filterField}
            value={state.localeId}
            onChange={(e) => patch({ localeId: e.target.value })}
          >
            <MenuItem value="">Any language</MenuItem>
            {locales.map((l) => (
              <MenuItem key={l.code} value={l.code}>
                {l.code} — {l.englishName || l.nativeName}
                {l.enabled ? "" : " (not offered yet)"}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            variant="outlined"
            size="small"
            className={classes.filterField}
            value={state.status}
            onChange={(e) => patch({ status: e.target.value })}
          >
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Deleted</MenuItem>
            <MenuItem value="ALL">All</MenuItem>
          </TextField>
          <TextField
            label="Search"
            placeholder="Name, description, subject, language"
            variant="outlined"
            size="small"
            className={classes.filterField}
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            inputProps={{ "aria-label": "Search message templates" }}
          />
        </Paper>

        <TableContainer component={Paper}>
          <Table
            size="small"
            aria-label="Message templates"
            aria-busy={loading}
          >
            <TableHead>
              <TableRow>
                {SORTABLE.map((col) => (
                  <TableCell
                    key={col.key}
                    className={classes.headerCell}
                    sortDirection={
                      state.sort === col.key
                        ? state.isAscending
                          ? "asc"
                          : "desc"
                        : false
                    }
                  >
                    <TableSortLabel
                      active={state.sort === col.key}
                      direction={
                        state.sort === col.key && !state.isAscending
                          ? "desc"
                          : "asc"
                      }
                      onClick={() => onSort(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell className={classes.headerCell}>Languages</TableCell>
                <TableCell className={classes.headerCell}>Channel</TableCell>
                <TableCell className={classes.headerCell}>Status</TableCell>
                <TableCell className={classes.headerCell}>
                  <span className="sr-only">Actions</span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box className={classes.progressWrap}>
                      <CircularProgress aria-label="Loading message templates" />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className={classes.empty}>
                    No message templates match these filters.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell className={`${classes.cell} ${classes.mono}`}>
                      {row.templateName}
                      {definitionByName[row.templateName]?.declared ===
                        false && (
                        <Chip
                          size="small"
                          label="not in this build"
                          style={{ marginLeft: 6 }}
                          title="This build has no definition for this name; variables are not checked."
                        />
                      )}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {row.description || "—"}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {auditLine(row.updatedAt, row.updatedByName)}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <ul
                        className={classes.languages}
                        aria-label={`Languages of ${row.templateName}`}
                      >
                        {(row.variants || []).map((v) => (
                          <li key={v.localeId}>
                            <Chip
                              size="small"
                              clickable
                              component="a"
                              href={`${PATHS.MESSAGE_TEMPLATES}/${
                                row.id
                              }/${encodeURIComponent(v.localeId)}`}
                              onClick={(e) => {
                                e.preventDefault();
                                go(
                                  `${PATHS.MESSAGE_TEMPLATES}/${
                                    row.id
                                  }/${encodeURIComponent(v.localeId)}`,
                                );
                              }}
                              variant={v.active ? "default" : "outlined"}
                              className={
                                state.localeId &&
                                v.localeId.toLowerCase() ===
                                  state.localeId.toLowerCase()
                                  ? classes.languageMatch
                                  : undefined
                              }
                              label={`${v.localeId}${
                                v.templateFormat === "PRINTF" ? " (%s)" : ""
                              }`}
                              title={[
                                v.active ? null : "Deleted language",
                                v.subject ? `Subject: ${v.subject}` : null,
                                v.templateFormat === "PRINTF"
                                  ? "Legacy %s format; opening and saving converts it"
                                  : null,
                                `Revision ${v.revision}`,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                              aria-label={`Edit ${row.templateName} in ${
                                v.localeId
                              }${v.active ? "" : " (deleted)"}`}
                            />
                          </li>
                        ))}
                      </ul>
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {CHANNEL_LABELS[row.channel] || row.channel}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {row.active ? (
                        "Active"
                      ) : (
                        <Chip size="small" label="Deleted" />
                      )}
                      {row.pendingVariableRequests > 0 && (
                        <Chip
                          size="small"
                          label={`${
                            row.pendingVariableRequests
                          } requested variable${
                            row.pendingVariableRequests === 1 ? "" : "s"
                          }`}
                          style={{ marginLeft: 6 }}
                          title="Variables an admin used that the code does not supply yet. They are sent empty until a developer maps them."
                        />
                      )}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <Button
                        color="primary"
                        className={classes.touch}
                        onClick={() =>
                          go(`${PATHS.MESSAGE_TEMPLATES}/${row.id}`)
                        }
                        aria-label={`Open ${row.templateName} ${
                          row.description || ""
                        }`.trim()}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <ListPagination
            filters={{
              page: result.currentPage,
              limit: state.size,
              total: result.totalElements,
            }}
            fetchFunction={({ page, limit }) =>
              setState((prev) => ({
                ...prev,
                page,
                size: Number(limit) || DEFAULT_SIZE,
              }))
            }
          />
        </TableContainer>
      </Container>
    </Box>
  );
};

export default MessageTemplates;
